import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './DepositDetails.css';

interface TransactionDetails {
  id: string;
  type: 'deposit';
  symbol: string;
  amount: string;
  usdValue: string;
  status: 'completed' | 'pending' | 'confirmed';
  requestTime: string;
  completedTime: string;
  network: string;
  depositAddress: string;
  hash: string;
}

// Mock transaction data matching Figma design
const mockTransaction: TransactionDetails = {
  id: '1',
  type: 'deposit',
  symbol: 'USDT',
  amount: '100.00',
  usdValue: '100',
  status: 'completed',
  requestTime: '03/11/2025 - 16:41:01',
  completedTime: '03/11/2025 - 16:41',
  network: 'TRX Tron (TRC20)',
  depositAddress: 'TDjsoxomFksw4deP7e9orStmvSPGDqKFzx',
  hash: '1630a3f8e9b7c2d4a5f6e8d9c0b1a2e3d4f5a6b7c8d9e0f1bab26e'
};

const DepositDetails: React.FC = () => {
  const navigate = useNavigate();
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const transaction = mockTransaction;

  const handleBack = () => {
    navigate(-1);
  };

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleViewHistory = () => {
    navigate('/history-deposit');
  };

  const handleBackToWallet = () => {
    navigate('/dashboard');
  };

  const truncateHash = (hash: string) => {
    return `${hash.slice(0, 6)}...${hash.slice(-6)}`;
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'pending':
        return 'Pending';
      case 'confirmed':
        return 'Confirmed';
      default:
        return status;
    }
  };

  return (
    <div className="deposit-details-container">
      {/* Header */}
      <header className="deposit-details-header">
        <button className="deposit-details-back-button" onClick={handleBack} aria-label="Go back">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="deposit-details-title">Transaction details</h1>
        <button className="deposit-details-menu-button" aria-label="More options">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="3" r="1.5" fill="currentColor"/>
            <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
            <circle cx="8" cy="13" r="1.5" fill="currentColor"/>
          </svg>
        </button>
      </header>

      <div className="deposit-details-content">
        {/* Amount Section */}
        <div className="deposit-details-amount-section">
          <p className="deposit-details-type">Deposit {transaction.symbol}</p>
          <h2 className="deposit-details-amount">{transaction.amount} {transaction.symbol}</h2>
          <p className="deposit-details-usd">~ ${transaction.usdValue}</p>
        </div>

        {/* Progress Timeline */}
        <div className="deposit-details-timeline">
          {/* Step 1: Deposit Request */}
          <div className="deposit-details-timeline-step completed">
            <div className="deposit-details-timeline-indicator">
              <div className="deposit-details-timeline-dot">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="deposit-details-timeline-line"></div>
            </div>
            <div className="deposit-details-timeline-content">
              <p className="deposit-details-timeline-title">Deposit request</p>
              <p className="deposit-details-timeline-subtitle">{transaction.requestTime}</p>
            </div>
          </div>

          {/* Step 2: Confirmed */}
          <div className="deposit-details-timeline-step completed">
            <div className="deposit-details-timeline-indicator">
              <div className="deposit-details-timeline-dot">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="deposit-details-timeline-line"></div>
            </div>
            <div className="deposit-details-timeline-content">
              <p className="deposit-details-timeline-title">Confirmed</p>
              <p className="deposit-details-timeline-subtitle">Done</p>
            </div>
          </div>

          {/* Step 3: Completed */}
          <div className="deposit-details-timeline-step completed final">
            <div className="deposit-details-timeline-indicator">
              <div className="deposit-details-timeline-dot">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <div className="deposit-details-timeline-content">
              <p className="deposit-details-timeline-title">Completed</p>
              <p className="deposit-details-timeline-subtitle">Credited to your balance</p>
            </div>
          </div>
        </div>

        {/* Transaction Details Card */}
        <div className="deposit-details-card">
          {/* Status Row */}
          <div className="deposit-details-card-row status-row">
            <span className="deposit-details-card-label">Status</span>
            <span className={`deposit-details-status-badge ${transaction.status}`}>
              {getStatusLabel(transaction.status)}
            </span>
          </div>

          {/* Completed Time Row */}
          <div className="deposit-details-card-row">
            <span className="deposit-details-card-label">Completed time</span>
            <span className="deposit-details-card-value">{transaction.completedTime}</span>
          </div>

          {/* Network Row */}
          <div className="deposit-details-card-row">
            <span className="deposit-details-card-label">Network</span>
            <span className="deposit-details-card-value">{transaction.network}</span>
          </div>

          {/* Deposit Address Row */}
          <div className="deposit-details-card-row address-row">
            <span className="deposit-details-card-label">Deposit address</span>
            <div className="deposit-details-card-value-with-copy">
              <span className="deposit-details-card-value address">{transaction.depositAddress}</span>
              <button 
                className="deposit-details-copy-button"
                onClick={() => handleCopy(transaction.depositAddress, 'address')}
                aria-label="Copy address"
              >
                {copiedField === 'address' ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="5.33333" y="5.33333" width="9.33333" height="9.33333" rx="1.33333" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M10.6667 5.33333V2.66667C10.6667 2.29848 10.3682 2 10 2H2.66667C2.29848 2 2 2.29848 2 2.66667V10C2 10.3682 2.29848 10.6667 2.66667 10.6667H5.33333" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Hash Row */}
          <div className="deposit-details-card-row hash-row">
            <span className="deposit-details-card-label">Hash</span>
            <div className="deposit-details-card-value-with-copy">
              <span className="deposit-details-card-value hash">{truncateHash(transaction.hash)}</span>
              <button 
                className="deposit-details-copy-button"
                onClick={() => handleCopy(transaction.hash, 'hash')}
                aria-label="Copy hash"
              >
                {copiedField === 'hash' ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="5.33333" y="5.33333" width="9.33333" height="9.33333" rx="1.33333" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M10.6667 5.33333V2.66667C10.6667 2.29848 10.3682 2 10 2H2.66667C2.29848 2 2 2.29848 2 2.66667V10C2 10.3682 2.29848 10.6667 2.66667 10.6667H5.33333" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="deposit-details-actions">
          <button className="deposit-details-action-button secondary" onClick={handleViewHistory}>
            History
          </button>
          <button className="deposit-details-action-button primary" onClick={handleBackToWallet}>
            Back to Wallet
          </button>
        </div>
      </div>
    </div>
  );
};

export default DepositDetails;